import { v4 as uuid } from "uuid";
import { UserService } from "../../../types/userService";

/**
 * Mock implementation of the User service for local development and testing
 */
class MockUserService implements UserService {
  private simulateDelay: () => Promise<void>;

  constructor(api_delay: number) {
    this.simulateDelay = () => {
      return new Promise((resolve) => {
        setTimeout(resolve, api_delay);
      });
    };
  }

  /**
   * Authenticate user with email and password
   */
  async login(
    email: string,
    _password: string,
  ): Promise<{ user: any; token: string }> {
    await this.simulateDelay();

    // Mock user data
    const user = {
      id: "user-123",
      email,
      name: "Demo User",
      role: "user",
    };

    // Mock token
    const token = `mock-token-${uuid()}`;

    console.log("Mock login for user:", email);
    return { user, token };
  }

  /**
   * Log user out
   */
  async logout(): Promise<void> {
    await this.simulateDelay();
    console.log("Mock user logout");
    // Just simulate a delay - no actual implementation
  }

  /**
   * Get current user profile
   */
  async fetchUserProfile(): Promise<any> {
    await this.simulateDelay();

    console.log("Mock fetch user profile");

    return {
      id: "user-123",
      name: "Demo User",
      email: "demo@example.com",
      role: "user",
      preferences: {
        darkMode: false,
        autoSave: true,
        defaultTreeLayout: "vertical",
      },
    };
  }

  /**
   * Update user profile
   */
  async updateUserProfile(profile: any): Promise<any> {
    await this.simulateDelay();

    console.log("Mock update user profile:", profile);

    // Just return the profile as if it was saved
    return {
      ...profile,
      updatedAt: new Date().toISOString(),
    };
  }

  // Mock checkout session creation
  async createCheckoutSession(
    _planType: string,
    _userEmail: string,
  ): Promise<{ url: string; session_id: string }> {
    await this.simulateDelay();
    const sessionId = `mock_session_${Math.random().toString(36).slice(2, 9)}`;
    return {
      url: `http://localhost:4242/checkout/${sessionId}`,
      session_id: sessionId,
    };
  }

  async createCheckoutSessionWithOptions(
    _planType: string,
    payload: {
      userEmail: string;
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ): Promise<{ url: string; session_id: string }> {
    await this.simulateDelay();
    const sessionId = `mock_session_${Math.random().toString(36).slice(2, 9)}`;
    let url = `http://localhost:4242/checkout/${sessionId}`;
    if (payload.coupon) url += `?coupon=${encodeURIComponent(payload.coupon)}`;
    return { url, session_id: sessionId };
  }

  async createSubscriptionCheckout(
    _tierId: string,
    payload?: {
      coupon?: string;
      billingRequired?: boolean;
      automaticTax?: boolean;
    },
  ): Promise<{ url: string; session_id?: string }> {
    await this.simulateDelay();
    const sessionId = `mock_sub_${Math.random().toString(36).slice(2, 9)}`;
    let url = `http://localhost:4242/checkout/${sessionId}`;
    if (payload?.coupon) url += `?coupon=${encodeURIComponent(payload.coupon)}`;
    return { url, session_id: sessionId };
  }

  async getSubscriptionStatus(): Promise<any> {
    await this.simulateDelay();
    // Return no subscription by default
    return { status: "none", has_active_subscription: false };
  }

  // Compatibility alias
  async fetchSubscriptionStatus(): Promise<any> {
    return this.getSubscriptionStatus();
  }

  async createPortalSession(): Promise<any> {
    await this.simulateDelay();
    return { url: "http://localhost:4242/customer-portal/mock" };
  }

  async getHasStripeCustomer(): Promise<{ has_customer: boolean }> {
    await this.simulateDelay();
    return { has_customer: false };
  }

  async verifySubscription(): Promise<{
    verified: boolean;
    status: string;
    tier?: string;
    source: string;
    has_active_subscription: boolean;
  }> {
    await this.simulateDelay();
    return {
      verified: false,
      status: "none",
      source: "mock",
      has_active_subscription: false,
    };
  }

  /**
   * Update user email (mock implementation)
   */
  async updateUserEmail(newEmail: string): Promise<any> {
    await this.simulateDelay();
    console.log("Mock update user email:", newEmail);
    return {
      email: newEmail,
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Change user password (mock implementation)
   */
  async changePassword(
    _currentPassword: string,
    _newPassword: string,
  ): Promise<void> {
    await this.simulateDelay();
    console.log("Mock change password");
    // Just simulate success
  }
}

export default MockUserService;
